import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useGeoLanguageDetection } from '@/hooks/useGeoLanguageDetection';

type Language = 'fr' | 'en' | 'es' | 'de' | 'it';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  availableLanguages: { code: Language; name: string; flag: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGES = [
  { code: 'fr' as Language, name: 'Français', flag: '🇫🇷' },
  { code: 'en' as Language, name: 'English', flag: '🇬🇧' },
  { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
  { code: 'de' as Language, name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it' as Language, name: 'Italiano', flag: '🇮🇹' },
];

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const { detectedLanguage, isDetecting } = useGeoLanguageDetection();
  
  // Priorité : choix manuel > langue détectée par IP > anglais
  const getInitialLanguage = (): Language => {
    const manualChoice = localStorage.getItem('language');
    if (manualChoice) return manualChoice as Language;
    
    if (detectedLanguage) return detectedLanguage;
    
    return 'en';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage());

  // Mettre à jour la langue quand la détection IP est terminée
  useEffect(() => {
    if (!isDetecting && detectedLanguage && !localStorage.getItem('language')) {
      setLanguageState(detectedLanguage);
      i18n.changeLanguage(detectedLanguage);
    }
  }, [detectedLanguage, isDetecting, i18n]);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem('language', lang);
    setLanguageState((prev) => (prev === lang ? prev : lang));
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      availableLanguages: LANGUAGES,
    }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
